import type { CSSProperties, FC } from 'react'

import { useEffect, useRef, useState } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    Pause,
    Play,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/kit/button'
import { Photo } from '@/components/kit/photo'
import { Spark } from '@/components/kit/spark'
import { showreel } from '@/constants/home'
import { START_HREF } from '@/constants/site'

interface ShowreelModalProps {
    open: boolean
    onClose: () => void
}

const SCENE_MS = 3400

/**
 * A short, self-playing showreel built from real case study moments.
 * Space pauses, the arrow keys move between scenes and Escape closes it.
 */
export const ShowreelModal: FC<ShowreelModalProps> = ({ open, onClose }) => {
    const [index, setIndex] = useState(0)
    const [playing, setPlaying] = useState(true)
    const [mounted, setMounted] = useState(false)
    const closeRef = useRef<HTMLButtonElement>(null)
    const returnFocus = useRef<HTMLElement | null>(null)
    const last = showreel.length - 1

    const go = (next: number) => setIndex((next + showreel.length) % showreel.length)

    const togglePlay = () => {
        if (!playing && index === last) setIndex(0)

        setPlaying(value => !value)
    }

    useEffect(() => {
        if (!open) return

        setMounted(true)
        returnFocus.current = document.activeElement as HTMLElement | null
        setIndex(0)
        setPlaying(!window.matchMedia('(prefers-reduced-motion: reduce)').matches)

        const frame = window.requestAnimationFrame(() => closeRef.current?.focus())
        const root = document.documentElement

        root.style.overflow = 'hidden'

        return () => {
            window.cancelAnimationFrame(frame)
            root.style.overflow = ''
            returnFocus.current?.focus()
        }
    }, [open])

    useEffect(() => {
        if (!open || !playing) return

        const timer = window.setTimeout(() => {
            setIndex(current => (current >= last ? current : current + 1))

            if (index >= last) setPlaying(false)
        }, SCENE_MS)

        return () => window.clearTimeout(timer)
    }, [open, playing, index, last])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
            if (event.key === 'ArrowRight') go(index + 1)
            if (event.key === 'ArrowLeft') go(index - 1)

            if (event.key === ' ' && !(event.target instanceof HTMLButtonElement)) {
                event.preventDefault()
                togglePlay()
            }
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, index, playing, onClose])

    return (
        <div
            role='dialog'
            aria-modal='true'
            aria-label='Updraft reel'
            inert={!open}
            className={cn(
                'fixed inset-0 z-[140] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-md transition-[opacity,visibility] duration-500 ease-expo md:p-10',
                open ? 'visible opacity-100' : 'invisible opacity-0'
            )}
            onClick={event => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <button
                ref={closeRef}
                type='button'
                onClick={onClose}
                aria-label='Close the reel'
                className='absolute right-4 top-4 z-20 flex size-12 items-center justify-center rounded-full bg-cream/10 text-cream transition-colors hover:bg-ember hover:text-ink md:right-8 md:top-8'
            >
                <X className='size-5' />
            </button>

            <div className={cn(
                'relative aspect-[9/16] max-h-[82vh] w-full max-w-[420px] overflow-hidden rounded-[1.75rem] bg-ink shadow-[0_40px_120px_-40px_rgb(0_0_0/0.8)] transition-transform duration-700 ease-expo md:aspect-video md:max-h-none md:max-w-6xl',
                open ? 'scale-100' : 'scale-95'
            )}>
                {mounted && showreel.map((scene, sceneIndex) => {
                    const active = sceneIndex === index

                    return (
                        <div
                            key={scene.title}
                            aria-hidden={!active}
                            className={cn(
                                'absolute inset-0 transition-opacity duration-700 ease-out',
                                active ? 'opacity-100' : 'opacity-0'
                            )}
                        >
                            {scene.image ? (
                                <div
                                    key={active ? `on-${index}` : 'off'}
                                    className='absolute inset-0'
                                    style={active && open ? { animation: `kenburns ${SCENE_MS + 800}ms linear forwards` } as CSSProperties : undefined}
                                >
                                    <Photo
                                        src={scene.image}
                                        alt=''
                                        sizes='(min-width: 768px) 1100px, 420px'
                                        position={scene.position}
                                        className='absolute inset-0 bg-ink'
                                    />
                                </div>
                            ) : (
                                <div className='grain grain-light absolute inset-0 bg-ink'>
                                    <div className='bg-grid-light absolute inset-0 opacity-60' />
                                    <Spark className='absolute right-[12%] top-[18%] size-10 animate-spin-slow text-ember' />
                                    <Spark className='absolute bottom-[22%] left-[10%] size-5 text-cream/40' />
                                </div>
                            )}

                            <div className='absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/30' />

                            <div className='absolute inset-x-0 bottom-0 p-6 pb-24 text-cream md:p-12 md:pb-28'>
                                <p className={cn('font-mono text-xs uppercase tracking-[0.2em] text-cream/80 transition-[opacity,translate] delay-200 duration-700 ease-expo', active ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0')}>
                                    {String(sceneIndex + 1).padStart(2, '0')} / {scene.eyebrow}
                                </p>

                                <p className={cn('mt-3 font-heading text-[clamp(2.75rem,8vw,7rem)] font-extrabold leading-[0.88] tracking-[-0.04em] transition-[opacity,translate] delay-300 duration-700 ease-expo', active ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0')}>
                                    {scene.title}
                                </p>

                                {scene.detail && (
                                    <p className={cn('mt-3 text-lg text-cream/80 transition-opacity delay-500 duration-700 md:text-2xl', active ? 'opacity-100' : 'opacity-0')}>
                                        {scene.detail}
                                    </p>
                                )}

                                {sceneIndex === last && (
                                    <div className={cn('mt-8 transition-opacity delay-500 duration-700', active ? 'opacity-100' : 'opacity-0')}>
                                        <Button
                                            href={START_HREF}
                                            size='lg'
                                            onClick={onClose}
                                        >
                                            Get a forecast
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}

                <div className='absolute inset-x-4 top-4 z-10 flex gap-1.5 md:inset-x-8 md:top-6'>
                    {showreel.map((scene, sceneIndex) => (
                        <span
                            key={scene.title}
                            className='h-[3px] flex-1 overflow-hidden rounded-full bg-cream/25'
                        >
                            <span
                                key={sceneIndex === index ? `run-${index}-${open}` : 'idle'}
                                className='block h-full origin-left rounded-full bg-cream'
                                style={{
                                    transform: sceneIndex < index ? 'scaleX(1)' : 'scaleX(0)',
                                    ...(sceneIndex === index && open
                                        ? {
                                            animation: `grow-x ${SCENE_MS}ms linear forwards`,
                                            animationPlayState: playing ? 'running' : 'paused'
                                        }
                                        : {})
                                } as CSSProperties}
                            />
                        </span>
                    ))}
                </div>

                <div className='absolute bottom-5 right-5 z-10 flex items-center gap-2 md:bottom-8 md:right-8'>
                    <button
                        type='button'
                        onClick={() => go(index - 1)}
                        aria-label='Previous scene'
                        className='flex size-11 items-center justify-center rounded-full bg-cream/12 text-cream backdrop-blur transition-colors hover:bg-cream hover:text-ink'
                    >
                        <ChevronLeft className='size-5' />
                    </button>
                    <button
                        type='button'
                        onClick={togglePlay}
                        aria-label={playing ? 'Pause the reel' : 'Play the reel'}
                        className='flex size-11 items-center justify-center rounded-full bg-ember text-ink transition-transform hover:scale-105'
                    >
                        {playing ? <Pause className='size-4 fill-current' /> : <Play className='size-4 translate-x-px fill-current' />}
                    </button>
                    <button
                        type='button'
                        onClick={() => go(index + 1)}
                        aria-label='Next scene'
                        className='flex size-11 items-center justify-center rounded-full bg-cream/12 text-cream backdrop-blur transition-colors hover:bg-cream hover:text-ink'
                    >
                        <ChevronRight className='size-5' />
                    </button>
                </div>
            </div>
        </div>
    )
}
